import React, { useEffect, useState } from "react";

/**
 * Desktop only. Tells the studio what a new version contains and lets them
 * decide when to take it.
 *
 * Nothing downloads until Download is clicked — a studio on a hotel connection
 * the night before a shoot should not discover the app helping itself to a
 * hundred megabytes. Never modal, always dismissible: an update must not
 * interrupt an upload mid-shoot, and the install waits for a restart they
 * choose (or the next natural quit).
 */

const CARD = {
	position: "fixed",
	bottom: 20,
	right: 20,
	zIndex: 9500,
	width: 340,
	background: "#0f172a",
	color: "#f1f5f9",
	borderRadius: 12,
	padding: "14px 16px",
	boxShadow: "0 10px 30px rgba(15,23,42,0.4)",
	fontSize: 14,
	lineHeight: 1.45,
};

const PRIMARY = {
	background: "#14b8a6",
	color: "#fff",
	border: "none",
	borderRadius: 8,
	padding: "7px 14px",
	fontWeight: 600,
	cursor: "pointer",
	fontSize: 13,
};

const QUIET = {
	background: "none",
	border: "none",
	color: "#94a3b8",
	cursor: "pointer",
	fontSize: 13,
	padding: "7px 4px",
};

export default function UpdateBanner() {
	const [state, setState] = useState(null); // { kind, version, notes, percent }
	const [dismissed, setDismissed] = useState(false);

	useEffect(() => {
		if (!window.photentra?.onUpdate) return undefined;
		return window.photentra.onUpdate((event) => {
			// The Help menu asks the renderer to run the check, so the same
			// banner reports the result wherever the check came from.
			if (event.kind === "menu-check") {
				setDismissed(false);
				setState({ kind: "checking" });
				window.photentra.checkForUpdate?.();
				return;
			}
			setDismissed(false);
			setState(event);
		});
	}, []);

	// "Up to date" and errors are answers to a question — say them, then go.
	useEffect(() => {
		if (state?.kind !== "none" && state?.kind !== "error") return undefined;
		const t = setTimeout(() => setState(null), 4000);
		return () => clearTimeout(t);
	}, [state]);

	if (!state || dismissed) return null;

	const close = (
		<button onClick={() => setDismissed(true)} aria-label="Dismiss" style={{ ...QUIET, fontSize: 16 }}>
			×
		</button>
	);

	const row = (children) => (
		<div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
			{children}
		</div>
	);

	if (state.kind === "checking") {
		return <div style={CARD}>{row(<><span>Checking for updates…</span>{close}</>)}</div>;
	}

	if (state.kind === "none") {
		return (
			<div style={CARD}>
				{row(<><span>Photentra {state.version} is up to date.</span>{close}</>)}
			</div>
		);
	}

	if (state.kind === "error") {
		return (
			<div style={CARD}>
				{row(<><span>Couldn’t check for updates. Try again later.</span>{close}</>)}
			</div>
		);
	}

	if (state.kind === "progress") {
		return (
			<div style={CARD}>
				<div style={{ marginBottom: 8 }}>Downloading update… {state.percent}%</div>
				<div style={{ background: "#1e293b", borderRadius: 999, height: 6, overflow: "hidden" }}>
					<div
						style={{
							width: `${state.percent || 0}%`,
							height: "100%",
							background: "#14b8a6",
							transition: "width 0.2s",
						}}
					/>
				</div>
			</div>
		);
	}

	// macOS: unsigned builds can't self-apply, so the app fetches nothing and
	// hands over the .dmg instead. Gatekeeper will refuse the first launch of an
	// un-notarised app, so the way past it is part of the offer rather than
	// something the studio has to go and find.
	if (state.kind === "manual") {
		return (
			<div style={CARD}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
					<strong style={{ fontSize: 14 }}>Photentra {state.version} is available</strong>
					{close}
				</div>

				{state.notes && (
					<div style={{ margin: "8px 0 4px", color: "#cbd5e1", fontSize: 13 }}>
						<div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 2 }}>What’s new</div>
						{state.notes.split("\n").slice(0, 5).map((line, i) => (
							<div key={i} style={{ whiteSpace: "pre-wrap" }}>{line}</div>
						))}
					</div>
				)}

				<div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
					<button
						style={PRIMARY}
						onClick={() => window.photentra.openExternal(state.url)}
					>
						Download for Mac
					</button>
					<button style={QUIET} onClick={() => setDismissed(true)}>Not now</button>
				</div>

				<div style={{ marginTop: 10, paddingTop: 9, borderTop: "1px solid #1e293b", color: "#94a3b8", fontSize: 12, lineHeight: 1.5 }}>
					Open the .dmg and drag Photentra to Applications, replacing the old one.
					The first launch is blocked — open <strong style={{ color: "#cbd5e1" }}>System&nbsp;Settings → Privacy&nbsp;&amp;&nbsp;Security</strong> and
					choose <strong style={{ color: "#cbd5e1" }}>Open&nbsp;Anyway</strong>.
				</div>
			</div>
		);
	}

	// An update is waiting, either to be fetched or to be applied.
	const ready = state.kind === "ready";
	return (
		<div style={CARD}>
			<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
				<strong style={{ fontSize: 14 }}>
					Photentra {state.version} {ready ? "is ready to install" : "is available"}
				</strong>
				{close}
			</div>

			{state.notes && (
				<div style={{ margin: "8px 0 4px", color: "#cbd5e1", fontSize: 13 }}>
					<div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 2 }}>What’s new</div>
					{state.notes.split("\n").slice(0, 5).map((line, i) => (
						<div key={i} style={{ whiteSpace: "pre-wrap" }}>{line}</div>
					))}
				</div>
			)}

			<div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
				{ready ? (
					<button style={PRIMARY} onClick={() => window.photentra.installUpdate()}>
						Restart to update
					</button>
				) : (
					<button style={PRIMARY} onClick={() => window.photentra.downloadUpdate()}>
						Download
					</button>
				)}
				<button style={QUIET} onClick={() => setDismissed(true)}>
					{ready ? "Later" : "Not now"}
				</button>
			</div>
		</div>
	);
}
